import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { Typography, IconButton } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { StyledErrorMessage } from "../style/modalStyle";
import { StyledLink } from "../style/ititn/itinUpload";
import CustomModal from "./CustomModal";
import PassportInstructionModal from "../itin/PassportInstructionModal";

interface FilePropType {
  title?: string;
  icon?: React.ReactNode;
  desc?: string;
  onChange?: (file: File | null) => void;
  fileUrl?: any;
  error?: boolean;
  helperText?: any;
  accept?: string;
  name?: string;
  showInstruction?: boolean;
  placeholder?: string;
}
const data = [
  {
    instructions: [
      "All four corners of the full passport should be visible. Both the top half and bottom half of the passport should included in the picture.",
      "The photo should be in color and not blurry.",
      "The passport should be valid least 6 months and not expired.",
      "Please scan your passport with the scanner.",
    ],
  },
];
const UploadFileField: React.FC<FilePropType> = ({
  title,
  icon,
  placeholder = "",
  onChange,
  fileUrl,
  error,
  name,
  helperText,
  showInstruction,
  accept = "image/jpeg,image/jpg,image/png", 
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | undefined>(fileUrl);
  const [fileError, setFileError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  useEffect(() => {
    if (fileUrl) {
      setPreviewUrl(fileUrl);
    }
  }, [fileUrl]);

  const validateFile = (selectedFile: File) => {
    const acceptedTypes = accept
      .split(",")
      .map((type) => type.trim().toLowerCase());
    if (!acceptedTypes.includes(selectedFile.type.toLowerCase())) {
      setFileError("Invalid file type.");
      return false;
    }
    setFileError(null);
    return true;
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files) return;
    const selectedFile = event.target.files[0];

    if (selectedFile && validateFile(selectedFile)) {
      setFile(selectedFile);
      setPreviewUrl(URL.createObjectURL(selectedFile));
      if (onChange) onChange(selectedFile);
    }
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragActive(false);
    if (event.dataTransfer.files.length > 0) {
      const droppedFile = event.dataTransfer.files[0];
      if (validateFile(droppedFile)) {
        setFile(droppedFile);
        setPreviewUrl(URL.createObjectURL(droppedFile));
        if (onChange) onChange(droppedFile);
      }
    }
  };


  const removeFile = () => {
    setFile(null);
    setPreviewUrl(undefined);
    setFileError(null); 

    const fileInput = document.getElementById(
      `file-upload-${title}`
    ) as HTMLInputElement;
    if (fileInput) {
      fileInput.value = ""; 
    }

    if (onChange) onChange(null);
  };

  return (
    <Wrapper>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          border: error ? "1px solid red" : "1px solid transparent",
        }}
      >
        <Title variant="h4">{title}</Title>
        {showInstruction && (
          <StyledLink to="" onClick={() => setIsModalOpen(true)}>
            Passport Instruction
          </StyledLink>
        )}
      </div>
      <DropArea
        onClick={() => document.getElementById(`file-upload-${title}`)?.click()}
        onDrop={handleDrop}
        onDragOver={(e: React.DragEvent<HTMLDivElement>) => {
          e.preventDefault();
          setDragActive(true);
        }}
        onDragLeave={(e: React.DragEvent<HTMLDivElement>) => {
          e.preventDefault();
          setDragActive(false);
        }}
        dragActive={dragActive}
      >
        <UploadContainer>
          {icon}
          <StyledTypography>
            {placeholder !== "" ? placeholder : title !== "Scan Passport" ? (
              <>
                <span>Click here</span> or drag & drop your certificate
              </>
            ) : (
              "Scan your passport"
            )}
          </StyledTypography>
          <HiddenInput
            type="file"
            id={`file-upload-${title}`}
            onChange={handleFileChange}
            accept={accept} // 🔹 Restrict accepted file types
          />
        </UploadContainer>
      </DropArea>
      {error && <span className="text-red-500">{helperText}</span>}
      {fileError && <StyledErrorMessage>{fileError}</StyledErrorMessage>}
      {previewUrl && (
        <FilePreview>
          <Typography
            sx={{
              overflowX: "auto",
            }}
          >
            {file
              ? file.name
              : fileUrl
              ? decodeURIComponent(fileUrl.split("/").pop() || "Uploaded File")
              : "Uploaded File"}
          </Typography>

          {/* {previewUrl.startsWith("http") ? (
            <a href={previewUrl} target="_blank" rel="noopener noreferrer">
              View File
            </a>
          ) : (
            ""
            // <img src={previewUrl} alt="Preview" width={100} />
          )} */}
          <IconButton onClick={removeFile}>
            <DeleteIcon color="error" />
          </IconButton>
        </FilePreview>
      )}
      <PassportInstructionModal
        open={isModalOpen}
        handleClose={() => setIsModalOpen(false)}
        title="Passport Upload Instruction"
        data={data}
      />
    </Wrapper>
  );
};

export default UploadFileField;

const Wrapper = styled.div`
  background: white;
  padding: 24px;
  border-radius: 10px;
  box-shadow: 0px 2px 40px 0px #dde3ea33;
`;

const Title = styled(Typography)`
  font-size: 16px !important;
  font-weight: 500 !important;
  line-height: 20px !important;
`;

const DropArea = styled.div<{ dragActive: boolean }>`
  margin-top: 10px;
  border: 0.6px dashed
    ${({ dragActive }) => (dragActive ? "#ff5722" : "#2977cc")};
  border-radius: 10px;
  padding: 40px;
  background: ${({ dragActive }) => (dragActive ? "#ffe0b2" : "#eaf1fa")};
  text-align: center;
  cursor: pointer;
  transition: 0.3s;
`;

const UploadContainer = styled.div`
  // min-width: 398px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  flex-direction: column;
  cursor: pointer;
  @media (max-width: 640px) {
    min-width: 0;
  }
`;

const StyledTypography = styled(Typography)`
  font-size: 14px;
  font-weight: 400;
  color: #2c3034;
  cursor: pointer;

  span {
    font-size: 14px;
    font-weight: 600;
    color: #2977cc;
  }
`;

const HiddenInput = styled.input`
  display: none;
`;

const FilePreview = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 10px;
  background: #f5f5f5;
  padding: 10px;
  border-radius: 5px;
  align-items: center;

  img {
    border-radius: 5px;
  }

  a {
    color: #2977cc;
    text-decoration: none;
    font-size: 14px;
  }
`;
